import { addMonths, addDays, addYears, isAfter, isBefore, differenceInMonths, format, parseISO } from 'date-fns';
import newPointsData from '../data/points-charts.json';
import { calculatePointsFromNewSchema, getResortCodeFromName } from './dataTransform.js';

// DVC Business Logic Functions

export class DVCContract {
  constructor(id, homeResort, useYearMonth, annualPoints, contractYear, pointsRemaining = {}) {
    this.id = id;
    this.homeResort = homeResort;
    this.useYearMonth = useYearMonth; // 1-12 representing month
    this.annualPoints = annualPoints;
    this.contractYear = contractYear;
    // Points remaining by use year
    this.pointsRemaining = {
      2025: pointsRemaining[2025] || annualPoints,
      2026: pointsRemaining[2026] || annualPoints,
      2027: pointsRemaining[2027] || annualPoints,
      ...pointsRemaining
    };
  }

  // Get the use year start date for a given calendar year
  getUseYearStart(calendarYear) {
    return new Date(calendarYear, this.useYearMonth - 1, 1);
  }

  // Get the use year end date for a given calendar year
  getUseYearEnd(calendarYear) {
    return new Date(calendarYear + 1, this.useYearMonth - 1, 0);
  }

  // Get current use year based on today's date
  getCurrentUseYear() {
    const today = new Date();
    const currentYear = today.getFullYear();
    const useYearStart = this.getUseYearStart(currentYear);
    
    if (isAfter(today, useYearStart)) {
      return currentYear;
    } else {
      return currentYear - 1;
    }
  }

  // Get points remaining for a specific use year
  getPointsRemaining(useYear) {
    return this.pointsRemaining[useYear] || 0;
  }

  // Get total available points across all tracked years
  getTotalAvailablePoints() {
    return Object.values(this.pointsRemaining).reduce((total, points) => total + points, 0);
  }

  // Get maximum spendable points for a specific use year (including borrowing potential)
  getMaxSpendablePoints(useYear) {
    const currentPoints = this.getPointsRemaining(useYear);
    const previousYear = useYear - 1;
    const previousYearPoints = this.getPointsRemaining(previousYear);
    const nextYear = useYear + 1;
    const nextYearPoints = this.getPointsRemaining(nextYear);
    
    // Can borrow all available points from next year, and bank any points from current year. 
    return currentPoints + nextYearPoints + previousYearPoints;
  }

  // Get detailed breakdown of available points by source
  getPointsBreakdown(useYear) {
    const currentPoints = this.getPointsRemaining(useYear);
    const previousYear = useYear - 1;
    const bankedPoints = this.getPointsRemaining(previousYear); // Points that can be banked from previous year
    const nextYear = useYear + 1;
    const borrowablePoints = this.getPointsRemaining(nextYear); // Points that can be borrowed from next year
    
    return {
      current: currentPoints,
      banked: bankedPoints,
      borrowable: borrowablePoints,
      total: currentPoints + bankedPoints + borrowablePoints
    };
  }

  // Get points that could be banked from a specific use year
  getBankablePoints(useYear) {
    const currentPoints = this.getPointsRemaining(useYear);
    
    // Can bank all unused points
    return currentPoints;
  }

  // Check if points can be banked (must be done by 4 months before use year ends)
  canBankPoints(useYear) {
    const today = new Date();
    const useYearEnd = this.getUseYearEnd(useYear);
    const bankingDeadline = addMonths(useYearEnd, -4);
    
    return isBefore(today, bankingDeadline);
  }

  // Bank unused points to next year
  bankPoints(pointsToBank, useYear) {
    if (!this.canBankPoints(useYear)) {
      throw new Error('Banking deadline has passed for this use year');
    }
    
    const currentPoints = this.getPointsRemaining(useYear);
    if (pointsToBank > currentPoints) {
      throw new Error('Cannot bank more points than available');
    }
    
    // Bank the points by reducing current year and adding to next year
    this.pointsRemaining[useYear] -= pointsToBank;
    this.pointsRemaining[useYear + 1] = (this.pointsRemaining[useYear + 1] || 0) + pointsToBank;
    
    return true;
  }

  // Borrow points from next year
  borrowPoints(pointsToBorrow, fromYear, toYear) {
    const availableFromYear = this.getPointsRemaining(fromYear);
    if (pointsToBorrow > availableFromYear) {
      throw new Error(`Cannot borrow more than ${availableFromYear} points from ${fromYear}`);
    }
    
    // Move points from future year to current year
    this.pointsRemaining[fromYear] -= pointsToBorrow;
    this.pointsRemaining[toYear] = (this.pointsRemaining[toYear] || 0) + pointsToBorrow;
    
    return true;
  }
}

// Booking window logic
export const getBookingWindow = (checkInDate, homeResort, targetResort) => {
  const today = new Date();
  const isHomeResort = homeResort === targetResort;
  
  // Home resort: 11 months in advance
  // Other resorts: 7 months in advance
  const monthsInAdvance = isHomeResort ? 11 : 7;
  const bookingOpenDate = addMonths(checkInDate, -monthsInAdvance);
  
  return {
    bookingOpenDate,
    canBookNow: isAfter(today, bookingOpenDate) || today.getTime() === bookingOpenDate.getTime(),
    isHomeResort,
    monthsInAdvance
  };
};

// Calculate points required for a stay using points-charts.json data
export const calculateStayPoints = (stay) => {
  const { resort, checkIn, lengthOfStay, roomType, viewType, year } = stay;
  
  if (!resort || !checkIn || !lengthOfStay || !roomType) {
    return 0;
  }


  // Parse check-in date
  const checkInDate = typeof checkIn === 'string' ? parseISO(checkIn) : checkIn;
  
  // Determine which points chart year to use
  let pointsChartYear;
  let originalYear;
  if (year) {
    // If year is explicitly provided, use it but cap at 2026 for future dates
    originalYear = year;
    pointsChartYear = year > 2026 ? 2026 : year;
  } else {
    // If no year provided, derive from checkIn date
    const checkInYear = checkInDate.getFullYear();
    originalYear = checkInYear;
    pointsChartYear = checkInYear > 2026 ? 2026 : checkInYear;
  }


  // Find the resort data in points-charts.json
  const resortData = newPointsData.find(r => {
    const code = getResortCodeFromName(r.ResortName);
    return code === resort && r.Year === pointsChartYear;
  });
  
  if (!resortData) {
    return 0;
  }
  
  // Find the exact room configuration that matches our room type and view
  const roomConfig = resortData.Rooms.find(room => {
    return room.type === roomType && (!viewType || room.view === viewType);
  });
  
  if (!roomConfig) {
    return 0;
  }

  // Find the travel period that contains our check-in date
  // If using a different year's chart, map the travel periods to the target year
  const yearOffset = originalYear - pointsChartYear;
  
  const matchingPeriod = roomConfig.TravelPeriods.find(period => {
    const startDate = parseISO(period.Start);
    const endDate = parseISO(period.End);
    
    // Map the travel period dates to the target year if needed
    const mappedStartDate = yearOffset !== 0 ? addYears(startDate, yearOffset) : startDate;
    const mappedEndDate = yearOffset !== 0 ? addYears(endDate, yearOffset) : endDate;
    
    return checkInDate >= mappedStartDate && checkInDate <= mappedEndDate;
  });
  
  if (!matchingPeriod) {
    return 0;
  }

  // Calculate total points based on each night of the stay
  let totalPoints = 0;
  for (let i = 0; i < lengthOfStay; i++) {
    const currentDate = addDays(checkInDate, i);
    const dayOfWeek = currentDate.getDay(); // 0 = Sunday, 6 = Saturday
    const isWeekend = dayOfWeek === 5 || dayOfWeek === 6; // Friday or Saturday
    
    // Check if this night falls in a different travel period
    let nightPeriod = matchingPeriod;
    for (const period of roomConfig.TravelPeriods) {
      const startDate = parseISO(period.Start);
      const endDate = parseISO(period.End);
      
      // Map the travel period dates to the target year if needed
      const mappedStartDate = yearOffset !== 0 ? addYears(startDate, yearOffset) : startDate;
      const mappedEndDate = yearOffset !== 0 ? addYears(endDate, yearOffset) : endDate;
      
      if (currentDate >= mappedStartDate && currentDate <= mappedEndDate) {
        nightPeriod = period;
        break;
      }
    }
    
    const pointsThisNight = isWeekend ? nightPeriod.FriToSat : nightPeriod.SunToThur;
    totalPoints += pointsThisNight;
  }
  
  return totalPoints;
};

// Helper function to determine travel period based on check-in date using points-charts.json
export const getTravelPeriod = (checkInDate, resortCode, year) => {
  const resortData = newPointsData.find(r => {
    const code = getResortCodeFromName(r.ResortName);
    return code === resortCode && r.Year === year;
  });
  
  if (!resortData) return null;
  
  const checkIn = typeof checkInDate === 'string' ? parseISO(checkInDate) : checkInDate;
  
  // Find matching travel period from any room configuration
  for (const room of resortData.Rooms) {
    for (const period of room.TravelPeriods) {
      const startDate = parseISO(period.Start);
      const endDate = parseISO(period.End);
      
      if (checkIn >= startDate && checkIn <= endDate) {
        return period.Name;
      }
    }
  }
  
  return null;
};

// Helper function to determine day type based on check-in date and length of stay
export const getDayType = (checkInDate, lengthOfStay) => {
  const checkIn = typeof checkInDate === 'string' ? parseISO(checkInDate) : checkInDate;
  
  if (lengthOfStay >= 7) {
    return 'Weekly';
  }
  
  const dayOfWeek = checkIn.getDay(); // 0 = Sunday, 6 = Saturday
  
  // If checking in Friday or Saturday, use weekend pricing
  if (dayOfWeek === 5 || dayOfWeek === 6) {
    return 'Fri - Sat';
  }
  
  // Otherwise use weekday pricing
  return 'Sun - Thu';
};


// Calculate total points needed for a vacation
export const calculateVacationPoints = (stays) => {
  return stays.reduce((total, stay) => {
    // Use the stored pointsRequired if available, otherwise calculate
    const stayPoints = stay.pointsRequired || calculateStayPoints(stay);
    console.log(`Stay ${stay.id}: stored=${stay.pointsRequired}, calculated=${calculateStayPoints(stay)}, using=${stayPoints}`);
    return total + stayPoints;
  }, 0);
};

// Allocate points from a contract with priority: banked > current > borrowed
// Uses contractData which tracks remaining points across all vacations
const allocatePointsFromContract = (contractData, pointsNeeded) => {
  const allocation = {
    contractId: contractData.contract.id,
    homeResort: contractData.contract.homeResort,
    totalPoints: 0,
    banked: 0,
    current: 0,
    borrowed: 0,
    useYear: contractData.useYear
  };

  let remaining = pointsNeeded;

  // Priority 1: Use banked points (from previous year)
  if (remaining > 0 && contractData.bankedRemaining > 0) {
    const bankedUsed = Math.min(remaining, contractData.bankedRemaining);
    allocation.banked = bankedUsed;
    allocation.totalPoints += bankedUsed;
    contractData.bankedRemaining -= bankedUsed;
    remaining -= bankedUsed;
  }

  // Priority 2: Use current year points
  if (remaining > 0 && contractData.currentRemaining > 0) {
    const currentUsed = Math.min(remaining, contractData.currentRemaining);
    allocation.current = currentUsed;
    allocation.totalPoints += currentUsed;
    contractData.currentRemaining -= currentUsed;
    remaining -= currentUsed;
  }

  // Priority 3: Borrow from next year (last resort)
  if (remaining > 0 && contractData.borrowableRemaining > 0) {
    const borrowedUsed = Math.min(remaining, contractData.borrowableRemaining);
    allocation.borrowed = borrowedUsed;
    allocation.totalPoints += borrowedUsed;
    contractData.borrowableRemaining -= borrowedUsed;
    remaining -= borrowedUsed;
  }

  // Update total available for tracking
  contractData.available = contractData.bankedRemaining + contractData.currentRemaining + contractData.borrowableRemaining;

  return {
    allocation,
    pointsAllocated: allocation.totalPoints,
    remainingNeeded: remaining
  };
};

// Check if contracts have enough points for planned vacations
export const checkPointAvailability = (contracts, plannedVacations) => {
  const contractPoints = {};
  
  // Debug logging
  if (process.env.NODE_ENV === 'development') {
    console.log('checkPointAvailability called with:');
    console.log('Contracts:', contracts.length);
    console.log('Vacations:', plannedVacations.length);
  }
  
  // Initialize available points for each contract with detailed breakdown
  contracts.forEach(contract => {
    const currentUseYear = contract.getCurrentUseYear();
    const breakdown = contract.getPointsBreakdown(currentUseYear);
    
    // Debug logging
    if (process.env.NODE_ENV === 'development') {
      console.log(`Contract ${contract.homeResort}: ${breakdown.total} max spendable points for use year ${currentUseYear} (${breakdown.banked} banked + ${breakdown.current} current + ${breakdown.borrowable} borrowable)`);
    }
    
    contractPoints[contract.id] = {
      contract,
      available: breakdown.total,
      allocated: 0,
      useYear: currentUseYear,
      // Track remaining points by category across all vacations
      bankedRemaining: breakdown.banked,
      currentRemaining: breakdown.current,
      borrowableRemaining: breakdown.borrowable
    };
  });
  
  // Sort vacations by check-in date to prioritize earlier bookings
  const sortedVacations = [...plannedVacations].sort((a, b) => 
    new Date(a.checkIn) - new Date(b.checkIn)
  );
  
  const results = [];
  
  sortedVacations.forEach(vacation => {
    const pointsNeeded = calculateVacationPoints(vacation.stays);
    let pointsAllocated = 0;
    const allocations = [];
    
    // Determine the use year for this vacation based on check-in date
    const checkInDate = new Date(vacation.checkIn);
    const vacationYear = checkInDate.getFullYear();
    
    // Debug logging
    if (process.env.NODE_ENV === 'development') {
      console.log(`\nProcessing vacation "${vacation.name}": ${pointsNeeded} points needed for ${vacationYear}`);
    }
    
    // Update contract use years for this vacation (but keep existing remaining points)
    Object.values(contractPoints).forEach(contractData => {
      // Determine which use year this vacation falls into for this contract
      const contract = contractData.contract;
      const useYearStart = contract.getUseYearStart(vacationYear);
      const useYearEnd = contract.getUseYearEnd(vacationYear);
      
      let vacationUseYear;
      if (checkInDate >= useYearStart && checkInDate <= useYearEnd) {
        vacationUseYear = vacationYear;
      } else if (checkInDate < useYearStart) {
        vacationUseYear = vacationYear - 1;
      } else {
        vacationUseYear = vacationYear + 1;
      }
      
      // Only update use year if it's different (and recalculate breakdown if needed)
      if (contractData.useYear !== vacationUseYear) {
        const breakdown = contract.getPointsBreakdown(vacationUseYear);
        contractData.useYear = vacationUseYear;
        contractData.bankedRemaining = breakdown.banked;
        contractData.currentRemaining = breakdown.current;
        contractData.borrowableRemaining = breakdown.borrowable;
        contractData.available = breakdown.total;
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`  ${contract.homeResort}: ${contractData.available} points available for use year ${vacationUseYear} (${contractData.bankedRemaining}B + ${contractData.currentRemaining}C + ${contractData.borrowableRemaining}Br)`);
      }
    });
    
    // Try to allocate points from contracts, prioritizing home resort contracts
    const sortedContracts = Object.values(contractPoints).sort((a, b) => {
      const aIsHome = vacation.stays.some(stay => stay.resort === a.contract.homeResort);
      const bIsHome = vacation.stays.some(stay => stay.resort === b.contract.homeResort);
      
      if (aIsHome && !bIsHome) return -1;
      if (!aIsHome && bIsHome) return 1;
      return b.available - a.available; // Then by available points
    });
    
    // Use smart allocation with banking/borrowing priority
    let remainingNeeded = pointsNeeded;
    
    for (const contractData of sortedContracts) {
      if (remainingNeeded <= 0) break;
      
      const result = allocatePointsFromContract(
        contractData, 
        remainingNeeded
      );
      
      if (result.pointsAllocated > 0) {
        allocations.push(result.allocation);
        pointsAllocated += result.pointsAllocated;
        remainingNeeded = result.remainingNeeded;
        
        // contractData is already updated by allocatePointsFromContract
        contractData.allocated += result.pointsAllocated;
        
        // Debug logging
        if (process.env.NODE_ENV === 'development') {
          const alloc = result.allocation;
          console.log(`  ${alloc.homeResort}: ${alloc.totalPoints} points (${alloc.banked} banked + ${alloc.current} current + ${alloc.borrowed} borrowed)`);
        }
      }
    }
    
    // Debug logging
    if (process.env.NODE_ENV === 'development') {
      console.log(`Result: ${pointsAllocated}/${pointsNeeded} allocated, shortfall: ${Math.max(0, pointsNeeded - pointsAllocated)}`);
    }
    
    results.push({
      vacation,
      pointsNeeded,
      pointsAllocated,
      allocations,
      isFullyFunded: pointsAllocated >= pointsNeeded,
      shortfall: Math.max(0, pointsNeeded - pointsAllocated)
    });
  });
  
  return {
    vacationResults: results,
    contractSummary: contractPoints
  };
};

// Get booking timeline for a vacation
export const getBookingTimeline = (vacation, contracts) => {
  const timeline = [];
  
  vacation.stays.forEach(stay => {
    contracts.forEach(contract => {
      const bookingWindow = getBookingWindow(
        new Date(stay.checkIn),
        contract.homeResort,
        stay.resort
      );
      
      timeline.push({
        date: bookingWindow.bookingOpenDate,
        action: `Booking opens for ${stay.resort} (${bookingWindow.isHomeResort ? 'Home Resort' : 'Other Resort'})`,
        resort: stay.resort,
        contractId: contract.id,
        homeResort: contract.homeResort,
        canBookNow: bookingWindow.canBookNow,
        isHomeResort: bookingWindow.isHomeResort,
        stay
      });
    });
  });
  
  // Remove duplicates and sort by date
  const uniqueTimeline = timeline.filter((item, index, self) => 
    index === self.findIndex(t => 
      t.date.getTime() === item.date.getTime() && 
      t.resort === item.resort &&
      t.contractId === item.contractId
    )
  ).sort((a, b) => a.date - b.date);
  
  return uniqueTimeline;
};
